import React, { useState, useEffect } from 'react';
import { useVehicles } from 'services/useVehicles';
import { Tiling } from 'components/views/atoms/Tiling';
import { VehicleMonitorWidget } from './VehicleMonitorWidget';
import { useSelectedProject } from 'services/useSelectedProject';
import { useEnvironments } from 'services/useEnvironments';
import { useSelectedEnvironment } from 'services/useSelectedEnvironment';
import { Project } from 'core/models/project';
import { useScopeCheck } from 'services/useScope';
import { CautionText } from 'components/views/atoms/CautionText';
import { Vehicle } from 'core/models/vehicle';

// return Vehicle Ids strictly corresponding to the given Project Id
function useVehiclesStrictly(projectId: Project.Id | undefined) {
    const environments = useEnvironments(projectId);
    const environmentId = useSelectedEnvironment(environments)?.id;
    const vehicles = useVehicles(projectId, environmentId);

    const [isLoading, setLoading] = useState(false);
    const [prevProjectId, setEffectiveProjectId] = useState<Project.Id>();
    useEffect(() => {
        setEffectiveProjectId(prevId => {
            if (prevId != projectId) {
                setLoading(true);
            } else {
                setLoading(false);
            }
            return projectId;
        });
    }, [vehicles, projectId]);

    return [isLoading ? undefined : prevProjectId, isLoading ? undefined : environmentId, isLoading ? [] : vehicles] as const;
}

export type DashboardPageProps = {
    onVehicleSelect: (vehicle: Vehicle) => void;
}

const DashboardPageImpl = (props: DashboardPageProps) => {
    const { onVehicleSelect } = props;

    const project = useSelectedProject();

    const [projectId, environmentId, vehicles] = useVehiclesStrictly(project ? project.id : undefined);

    return (
        <Tiling>
            {
                projectId && environmentId
                    ? vehicles.map(vehicle =>
                        <VehicleMonitorWidget
                            key={vehicle.id}
                            vehicle={vehicle} projectId={projectId} environmentId={environmentId}
                            onTitleClick={() => onVehicleSelect(vehicle)} />
                    )
                    : []
            }
        </Tiling>
    );
};

export function DashboardPage(props: DashboardPageProps) {
    const isVehicleListAllowed = useScopeCheck('getVehiclesScope');

    if (isVehicleListAllowed === undefined) return <></>;

    return (
        isVehicleListAllowed
            ? <DashboardPageImpl {...props} />
            : (
                <Tiling>
                    <CautionText text={'You don\'t have permission to view the vehicles'} />
                </Tiling>
            )
    );
}
